/**
 * A lista da Inbox: páginas, ordem congelada e tempo real (H07).
 *
 * ── Duas fontes, pela mesma razão do histórico ────────────────────────────
 *
 * A primeira página é consulta viva, invalidada pelo SSE — é onde a mensagem
 * nova chega. As páginas seguintes ficam em estado e não são rebuscadas: a
 * rolagem contínua pode ter carregado dez delas, e rebuscar todas a cada
 * notificação custaria dez requisições por mensagem recebida numa revenda
 * movimentada.
 *
 * O preço é que uma conversa da página cinco pode ficar com a prévia velha até
 * o usuário aceitar a ordem nova. Vale: ela está longe da vista, e quem está
 * olhando a página cinco está procurando algo específico, não monitorando.
 *
 * ── E a ordem congela por cima disso ──────────────────────────────────────
 *
 * Ver `ordem-estavel.ts` para o porquê. Aqui só se combina: os dados vivem, a
 * posição espera.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ItemDaInbox, PaginaDaInbox } from '@cliqcrm/contracts';
import { useApi, useInbox } from '@/shared/api/consultas';
import {
  acrescentarPagina,
  aplicarOrdemEstavel,
  congelarDoServidor,
  congeladoVazio,
  type Congelado,
  type ItemOrdenado,
} from '../lib/ordem-estavel';
import { comoConsulta, type FiltrosDaInbox } from '../lib/filtros';

const idDe = (item: ItemDaInbox): string => item.conversa.id;

/**
 * O que conta como "esta conversa mudou".
 *
 * De propósito NÃO inclui `projetado_em`, que muda a cada passada da projeção
 * mesmo sem nada acontecer — com ele, a lista inteira ficaria marcada como
 * atualizada o tempo todo, e a marca deixaria de significar algo.
 */
const assinaturaDe = (item: ItemDaInbox): string =>
  [
    item.conversa.ultima_mensagem?.em ?? '',
    item.conversa.nao_lidas,
    item.conversa.situacao,
    item.conversa.prioridade ?? '',
    item.resumo_comercial?.etapa ?? '',
  ].join('|');

export interface ListaDaInbox {
  readonly itens: readonly ItemOrdenado<ItemDaInbox>[];
  readonly carregando: boolean;
  readonly erro: unknown;
  readonly totalNoEscopo: number | null;
  readonly frescor: PaginaDaInbox['frescor'] | null;
  /** Conversas que existem no servidor e ainda não entraram na lista. */
  readonly novas: number;
  /** Traz as novas e reordena. É a única coisa que move a lista. */
  readonly aceitarOrdem: () => void;
  readonly temMais: boolean;
  readonly carregandoMais: boolean;
  readonly carregarMais: () => void;
  readonly recarregar: () => void;
  /** As caixas presentes no que já foi carregado — alimenta o filtro. */
  readonly caixasConhecidas: readonly { id: string; nome: string }[];
  /** Os canais presentes no que já foi carregado — idem. */
  readonly canaisConhecidos: readonly string[];
}

export function useListaDaInbox(filtros: FiltrosDaInbox): ListaDaInbox {
  const api = useApi();
  const consulta = comoConsulta(filtros);
  const cabeca = useInbox(consulta);

  const [seguintes, definirSeguintes] = useState<readonly ItemDaInbox[]>([]);
  const [cursor, definirCursor] = useState<string | null>(null);
  const [temMaisAdiante, definirTemMaisAdiante] = useState(false);
  const [buscandoMais, definirBuscandoMais] = useState(false);
  const [congelado, definirCongelado] = useState<Congelado<ItemDaInbox>>(congeladoVazio);

  // A chave dos filtros, para saber quando eles realmente mudaram. O objeto é
  // novo a cada renderização; o texto não.
  const chaveDosFiltros = JSON.stringify(consulta);

  /**
   * Mudar filtro zera tudo, e é o momento certo de reordenar.
   *
   * A lista muda de composição de qualquer forma, então não há posição de
   * leitura a preservar — o usuário acabou de dizer que quer outra coisa.
   */
  useEffect(() => {
    definirSeguintes([]);
    definirCursor(null);
    definirTemMaisAdiante(false);
    definirCongelado(congeladoVazio());
  }, [chaveDosFiltros]);

  const doServidor = useMemo(
    () => [...(cabeca.data?.itens ?? []), ...seguintes],
    [cabeca.data, seguintes],
  );

  /**
   * O primeiro congelamento acontece sozinho.
   *
   * Sem ele a lista abriria vazia: `aplicarOrdemEstavel` com ordem vazia usa a
   * do servidor, mas nada ficaria conhecido, e a primeira notificação marcaria
   * tudo como novo.
   */
  const jaCongelou = useRef(false);
  useEffect(() => {
    if (congelado.ordem.length === 0 && doServidor.length > 0 && !jaCongelou.current) {
      jaCongelou.current = true;
      definirCongelado(congelarDoServidor(doServidor, idDe, assinaturaDe));
    }
  }, [congelado.ordem.length, doServidor]);

  useEffect(() => {
    jaCongelou.current = false;
  }, [chaveDosFiltros]);

  const ordenada = useMemo(
    () => aplicarOrdemEstavel(congelado, doServidor, idDe, assinaturaDe),
    [congelado, doServidor],
  );

  const aceitarOrdem = useCallback(() => {
    definirCongelado(congelarDoServidor(doServidor, idDe, assinaturaDe));
  }, [doServidor]);

  // ── Rolagem contínua ────────────────────────────────────────────────────

  const cursorDaCabeca = cabeca.data?.pagina.cursor_proximo ?? null;
  const temMaisNaCabeca = cabeca.data?.pagina.tem_mais ?? false;

  const cursorAtual = cursor ?? cursorDaCabeca;
  const temMais = cursor === null ? temMaisNaCabeca : temMaisAdiante;

  // Uma busca por vez: a rolagem dispara muitas vezes seguidas, e sem a trava o
  // mesmo cursor renderia meia dúzia de requisições com respostas iguais.
  const emCurso = useRef(false);

  const carregarMais = useCallback(() => {
    if (!api || !cursorAtual || emCurso.current) return;

    emCurso.current = true;
    definirBuscandoMais(true);

    void api
      .listarInbox({ consulta: { ...consulta, cursor: cursorAtual } })
      .then((pagina) => {
        definirSeguintes((atuais) => [...atuais, ...pagina.itens]);
        definirCursor(pagina.pagina.cursor_proximo);
        definirTemMaisAdiante(pagina.pagina.tem_mais);
        // A página nova entra no FIM da ordem congelada — o que está acima não
        // se mexe, que é o requisito de âncora estável da task.
        definirCongelado((atual) => acrescentarPagina(atual, pagina.itens, idDe, assinaturaDe));
      })
      .finally(() => {
        emCurso.current = false;
        definirBuscandoMais(false);
      });
  }, [api, cursorAtual, consulta]);

  const caixasConhecidas = useMemo(() => {
    const porId = new Map<string, string>();
    for (const { item } of ordenada.itens) {
      porId.set(item.caixa.referencia_externa, item.caixa.nome ?? item.caixa.referencia_externa);
    }
    return [...porId].map(([id, nome]) => ({ id, nome }));
  }, [ordenada.itens]);

  const canaisConhecidos = useMemo(() => {
    const vistos = new Set<string>();
    for (const { item } of ordenada.itens) {
      if (item.caixa.canal) vistos.add(item.caixa.canal);
    }
    return [...vistos];
  }, [ordenada.itens]);

  return {
    itens: ordenada.itens,
    carregando: cabeca.isPending,
    erro: cabeca.error,
    totalNoEscopo: cabeca.data?.pagina.total_no_escopo ?? null,
    frescor: cabeca.data?.frescor ?? null,
    novas: ordenada.novas,
    aceitarOrdem,
    temMais,
    carregandoMais: buscandoMais,
    carregarMais,
    recarregar: () => void cabeca.refetch(),
    caixasConhecidas,
    canaisConhecidos,
  };
}
